import React, { useEffect } from 'react';
import { App, Button, Card, Form, Input, InputNumber, Select, Space, Spin, Switch, Typography } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useOperation,
  useCreateOperation,
  useUpdateOperation,
} from '@/hooks/useManufacturingPhase2';
import { OperationType, SkillLevel } from '@/services/manufacturing-phase2.service';

const { Title, Text } = Typography;
const LIST_PATH = '/mfg/operations';

const OperationMasterFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm();

  const { data: operation, isLoading: loadingOperation, isError } = useOperation(id ?? '');
  const createOperation = useCreateOperation();
  const updateOperation = useUpdateOperation();

  useEffect(() => {
    if (!isEdit || !operation) return;
    form.setFieldsValue({
      operation_code: operation.operation_code,
      operation_name: operation.operation_name,
      operation_type: operation.operation_type,
      default_workstation_type: operation.default_workstation_type,
      standard_setup_time_hours: operation.standard_setup_time_hours,
      standard_run_time_per_unit_hours: operation.standard_run_time_per_unit_hours,
      standard_teardown_time_hours: operation.standard_teardown_time_hours,
      per_hour_cost: operation.per_hour_cost,
      skill_level_required: operation.skill_level_required,
      description: operation.description,
      is_active: operation.is_active,
    });
  }, [isEdit, operation, form]);

  useEffect(() => {
    if (!isEdit) {
      form.resetFields();
      form.setFieldsValue({
        standard_setup_time_hours: 0,
        standard_run_time_per_unit_hours: 0,
        standard_teardown_time_hours: 0,
        per_hour_cost: 0,
        skill_level_required: SkillLevel.BASIC,
        is_active: true,
      });
    }
  }, [isEdit, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (isEdit && id) {
        await updateOperation.mutateAsync({ id, data: values });
        queryClient.invalidateQueries({ queryKey: ['operation', id] });
      } else {
        await createOperation.mutateAsync(values);
      }
      navigate(LIST_PATH);
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      console.error(e);
    }
  };

  const saving = createOperation.isPending || updateOperation.isPending;

  if (isEdit && loadingOperation) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isEdit && (isError || !operation)) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text type="danger">Operation not found or failed to load.</Text>
          <div style={{ marginTop: 16 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(LIST_PATH)}>
              Back to list
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(LIST_PATH)}>
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {isEdit ? 'Edit operation' : 'New operation'}
          </Title>
        </Space>

        <Card>
          <Form form={form} layout="vertical" requiredMark="optional">
            <Form.Item
              name="operation_code"
              label="Operation code"
              rules={[{ required: true, message: 'Please enter operation code' }]}
            >
              <Input placeholder="e.g., OP-001" />
            </Form.Item>

            <Form.Item
              name="operation_name"
              label="Operation name"
              rules={[{ required: true, message: 'Please enter operation name' }]}
            >
              <Input placeholder="e.g., CNC Milling" />
            </Form.Item>

            <Form.Item
              name="operation_type"
              label="Operation type"
              rules={[{ required: true, message: 'Please select operation type' }]}
            >
              <Select placeholder="Select type">
                {Object.values(OperationType).map((type) => (
                  <Select.Option key={type} value={type}>
                    {type}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="default_workstation_type" label="Default workstation type">
              <Input placeholder="e.g., CNC Machine" />
            </Form.Item>

            <Space style={{ width: '100%' }} size="large" wrap>
              <Form.Item
                name="standard_setup_time_hours"
                label="Setup time (hours)"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} step={0.25} style={{ width: 160 }} />
              </Form.Item>

              <Form.Item
                name="standard_run_time_per_unit_hours"
                label="Run time per unit (hours)"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} step={0.0001} precision={4} style={{ width: 200 }} />
              </Form.Item>

              <Form.Item
                name="standard_teardown_time_hours"
                label="Teardown time (hours)"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} step={0.25} style={{ width: 160 }} />
              </Form.Item>

              <Form.Item
                name="per_hour_cost"
                label="Per hour cost"
                rules={[{ required: true }]}
                tooltip="Standard rate per hour (company currency) for labor or machine time."
              >
                <InputNumber min={0} step={0.01} precision={2} style={{ width: 160 }} />
              </Form.Item>
            </Space>

            <Form.Item
              name="skill_level_required"
              label="Skill level required"
              rules={[{ required: true }]}
            >
              <Select>
                {Object.values(SkillLevel).map((level) => (
                  <Select.Option key={level} value={level}>
                    {level}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={4} placeholder="Optional description…" />
            </Form.Item>

            <Form.Item name="is_active" label="Status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
                  {isEdit ? 'Save changes' : 'Create operation'}
                </Button>
                <Button onClick={() => navigate(LIST_PATH)}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

export default OperationMasterFormPage;
